import { Navigate } from "react-router-dom";
import {
  Accreditations,
  Bookings,
  Drivers,
  Clients,
  Companies,
  Payments,
  PrivateNetwork,
  Profile,
  RatingManager,
  TripReporting,
  ExternalSuppliers,
  Onboarding,
  Vehicles,
  LegacyVehicles,
  Suspended,
  SubscriptionRequired,
  AddOns,
  QuickSetup,
  SoloOperatorGetStarted,
} from "@/components/pages";
import { QuickSetupSkeleton } from "@/components/organisms/QuickSetup/components";
import { ManageClient, OperatorClientBooking, OperatorClientBookingConfirmation } from "@/components/pages/Clients/ManageClient";
import { useOperatorAccess } from "@/hooks";
import { RequirePermission } from "@/permissions";
import { EPermission } from "@/permissions/types";
import { ConditionalAccess } from "./ConditionalAccess.tsx/ConditionalAccess";

/**
 * One Quick Setup route for every plan: sole operators get the Solo checklist, everyone else the
 * Fleet one. Both are reached at /quick-setup so the sidebar needs a single entry.
 */
const QuickSetupIndex = () => {
  const { isSolo, isPlanResolved } = useOperatorAccess();
  // The plan is the only thing that decides this. Permissions decide access, and a fleet account
  // can still carry the old get-started one. Until the subscription query has settled, isSolo is
  // not yet meaningful, so hold the skeleton rather than guessing a panel.
  if (!isPlanResolved) return <QuickSetupSkeleton />;
  return isSolo ? <SoloOperatorGetStarted /> : <QuickSetup />;
};

/** Unsubscribed operators land on Subscriptions; everyone else uses the normal payments default. */
const PaymentsIndex = () => {
  const { isUnsubscribed } = useOperatorAccess();
  if (isUnsubscribed) return <Navigate to="subscriptions" replace />;
  return <ConditionalAccess action="view" subject="providerPayments" truthyPath="credit-card-payments" falsyPath="payment-configuration" />;
};

export const operatorRoutes = [
  {
    // Kept so bookmarks and any link still pointing at the old Solo-only path keep working.
    path: "get-started",
    element: <Navigate to="../quick-setup" replace />,
  },
  {
    path: "quick-setup",
    element: <RequirePermission anyOf={[EPermission.ProviderQuickSetupManage, EPermission.ProviderGetStartedManage]} />,
    handle: { pageName: "Quick Setup" },
    children: [
      {
        index: true,
        element: <QuickSetupIndex />,
      },
    ],
  },
  {
    path: "accreditations",
    element: <RequirePermission permission={EPermission.ProviderAccreditationsManage} />,
    handle: { pageName: "Accreditations" },
    children: [
      {
        index: true,
        element: <Accreditations />,
      },
      {
        path: ":id/history",
        handle: { pageName: "History Logs" },
        element: <Accreditations.History />,
      },
    ],
  },
  {
    path: "rating-manager",
    element: <RequirePermission permission={EPermission.ProviderRatingDriverManage} />,
    handle: { pageName: "Rating Manager" },
    children: [
      {
        index: true,
        element: <RatingManager />,
      },
    ],
  },
  {
    path: "drivers",
    element: <RequirePermission permission={EPermission.ProviderDriverManage} />,
    handle: { pageName: "Drivers" },
    children: [
      {
        path: "application-rules",
        handle: { pageName: "Application Rules" },
        element: <Drivers.ApplicationRules />,
      },
      {
        path: "*",
        element: <Drivers />,
        children: [
          {
            index: true,
            element: <Navigate to="my-drivers" replace />,
          },
          {
            path: "my-drivers",
            children: [
              {
                index: true,
                element: <Drivers.MyDrivers />,
              },
              {
                path: ":driverId",
                children: [
                  {
                    index: true,
                    element: <Navigate to="profile" replace />,
                  },
                  {
                    path: "profile",
                    element: <Drivers.MyDrivers.Profile />,
                  },
                  {
                    path: "vehicles",
                    element: <Drivers.MyDrivers.Vehicles />,
                    children: [
                      {
                        index: true,
                        element: <Navigate to="assigned" />,
                      },
                      {
                        path: "owned",
                        element: <Drivers.MyDrivers.Vehicles.OwnedVehicles />,
                      },
                      {
                        path: "assigned",
                        element: <Drivers.MyDrivers.Vehicles.AssignedVehicles />,
                      },
                    ],
                  },
                  {
                    path: "job-history",
                    element: <Drivers.MyDrivers.JobHistory />,
                  },
                  {
                    path: "payout-history",
                    element: <Drivers.MyDrivers.PayoutHistory />,
                  },
                  {
                    path: "admin",
                    element: <Drivers.MyDrivers.AdminActions />,
                  },
                  {
                    path: "history-logs",
                    element: <Drivers.MyDrivers.HistoryLogs />,
                  },
                ],
              },
            ],
          },
          {
            path: "offload",
            children: [
              {
                index: true,
                element: <Drivers.OffloadDrivers />,
              },
            ],
          },
          {
            path: "archived",
            children: [
              {
                index: true,
                element: <Drivers.ArchivedDrivers />,
              },
              {
                path: ":driverId",
                children: [
                  {
                    index: true,
                    element: <Navigate to="profile" replace />,
                  },
                  {
                    path: "profile",
                    element: <Drivers.ArchivedDrivers.Profile />,
                  },
                  {
                    path: "job-history",
                    element: <Drivers.ArchivedDrivers.JobHistory />,
                  },
                  {
                    path: "admin",
                    element: <Drivers.ArchivedDrivers.AdminActions />,
                  },
                  {
                    path: "history-logs",
                    element: <Drivers.ArchivedDrivers.HistoryLogs />,
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "external-suppliers",
    element: <RequirePermission permission={EPermission.ProviderExternalSupplierManage} />,
    handle: { pageName: "External Suppliers" },
    children: [
      {
        path: "*",
        element: <ExternalSuppliers />,
        children: [
          {
            index: true,
            element: <Navigate to="my-external-suppliers" replace />,
          },
          {
            path: "my-external-suppliers",
            children: [
              {
                index: true,
                element: <ExternalSuppliers.MyExternalSuppliers />,
              },
              {
                path: "add",
                handle: { pageName: "Add External Supplier" },
                element: <ExternalSuppliers.MyExternalSuppliers.Add />,
              },
              {
                path: ":externalSupplierId",
                children: [
                  {
                    index: true,
                    element: <Navigate to="profile" replace />,
                  },
                  {
                    path: "profile",
                    element: <ExternalSuppliers.MyExternalSuppliers.Profile />,
                  },
                ],
              },
            ],
          },
          {
            path: "archived-external-suppliers",
            children: [
              {
                index: true,
                element: <ExternalSuppliers.ArchivedExternalSuppliers />,
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "clients",
    element: <RequirePermission permission={EPermission.ProviderClientManage} />,
    handle: { pageName: "Clients" },
    children: [
      {
        index: true,
        element: <Clients />,
      },
      {
        path: "add",
        handle: { pageName: "Add New Client" },
        element: <Clients.Add />,
      },
      {
        path: ":clientId",
        handle: { pageName: "View Client" },
        element: <ManageClient />,
        children: [
          {
            index: true,
            element: <Navigate to="details" replace />,
          },
          {
            path: "details",
            element: <ManageClient.Details />,
          },
          {
            // EA / Travel-Manager only — the managed EA's client list, view-only. The tab is shown
            // only for EA/TM accounts, but the route stays mounted so a direct URL resolves.
            path: "ea-clients",
            element: <ManageClient.EaClients />,
          },
          {
            path: "favourites",
            element: <ManageClient.Favourites />,
            children: [
              {
                index: true,
                element: <Navigate to="locations" replace />,
              },
              {
                path: "locations",
                element: <ManageClient.Favourites.Locations />,
              },
              {
                path: "guests",
                element: <ManageClient.Favourites.Guests />,
              },
              {
                path: "drivers",
                element: <ManageClient.Favourites.Drivers />,
              },
            ],
          },
          {
            path: "settings",
            element: <ManageClient.Settings />,
            children: [
              {
                index: true,
                element: <Navigate to="profile" replace />,
              },
              {
                path: "profile",
                element: <ManageClient.Settings.Profile />,
              },
              {
                path: "notifications",
                element: <ManageClient.Settings.Notifications />,
              },
              {
                path: "payments",
                element: <ManageClient.Settings.Payments />,
              },
              {
                path: "assistants",
                element: <ManageClient.Settings.Assistants />,
              },
              {
                path: "admin-settings",
                element: <ManageClient.Settings.AdminSettings />,
              },
            ],
          },
        ],
      },
      {
        // Operator books on behalf of a client (DEV-462). Sibling to the `:clientId` tab shell,
        // not a child of it — the booking sheet is a full-bleed map surface, not a Manage-Client
        // tab. Same ProviderClientManage gate as the rest of `clients`.
        path: ":clientId/booking",
        handle: { pageName: "Book a Trip" },
        element: <OperatorClientBooking />,
      },
      {
        path: ":clientId/booking/confirmation",
        handle: { pageName: "Booking Confirmation" },
        element: <OperatorClientBookingConfirmation />,
      },
      {
        // View/edit an existing client booking (from the client-record Booking History row action).
        // One URL like the client portal — the sheet resolves view vs edit from the booking's own
        // `is_editable`. Same full-bleed sheet + ProviderClientManage gate as the create route above.
        path: ":clientId/booking/:jobId",
        handle: { pageName: "Booking Details" },
        element: <OperatorClientBooking />,
      },
    ],
  },
  {
    path: "companies",
    handle: { pageName: "Companies" },
    element: <RequirePermission permission={EPermission.ProviderCompanyView} />,
    children: [
      {
        index: true,
        element: <Companies />,
      },
      {
        path: ":id",
        children: [
          {
            index: true,
            handle: { pageKey: "companyName" },
            element: <Companies.View />,
          },
        ],
      },
      {
        path: ":id/*",
        element: <RequirePermission permission={EPermission.ProviderCompanyManage} />,
        children: [
          {
            path: "edit",
            handle: { pageName: "Update Company" },
            element: <Companies.Edit />,
          },
        ],
      },
      {
        path: "add",
        element: <RequirePermission permission={EPermission.ProviderCompanyManage} />,
        handle: { pageName: "Add New Company" },
        children: [
          {
            index: true,
            element: <Companies.Add />,
          },
        ],
      },
    ],
  },
  {
    path: "payments",
    element: <RequirePermission permission={EPermission.ProviderPaymentsView} />,
    handle: { pageName: "Payments" },
    children: [
      {
        path: "*",
        element: <Payments />,
        children: [
          {
            index: true,
            element: <PaymentsIndex />,
          },
          {
            path: "invoicing",
            element: <Payments.Invoicing />,
            children: [
              {
                index: true,
                element: <Navigate to="uninvoiced-jobs" />,
              },
              {
                path: "uninvoiced-jobs",
                element: <Payments.Invoicing.UninvoicedJobs />,
              },
              {
                path: "invoices",
                element: <Payments.Invoicing.Invoices />,
              },
            ],
          },
          {
            path: "credit-card-payments",
            element: <RequirePermission permission={EPermission.ProviderPaymentsView} />,
            children: [
              {
                index: true,
                element: <Payments.CreditCardPayments />,
              },
            ],
          },
          {
            path: "payout-history",
            element: <RequirePermission permission={EPermission.ProviderPaymentsView} />,
            children: [
              {
                index: true,
                element: <Payments.PayoutHistory />,
              },
              {
                path: ":id",
                handle: {
                  pageKey: "arrivalDate",
                },
                element: <Payments.PayoutHistory.Payout />,
              },
            ],
          },
          {
            path: "debit-history",
            element: <RequirePermission permission={EPermission.ProviderPaymentsView} />,
            children: [
              {
                index: true,
                element: <Payments.DebitHistory />,
              },
              {
                path: ":id",
                handle: {
                  pageKey: "creationDate",
                },
                element: <Payments.DebitHistory.DebitRecords />,
              },
            ],
          },
          {
            path: "payment-configuration",
            children: [
              {
                index: true,
                element: <Payments.PaymentConfiguration />,
              },
              {
                path: ":type?/:mode?",
                element: <Payments.PaymentConfiguration />,
              },
              {
                path: "debit/history",
                element: <Payments.PaymentConfiguration.DebitsHistory />,
                handle: { pageName: "Bank Details - Debits" },
              },
              {
                path: "deposit/history",
                element: <Payments.PaymentConfiguration.DepositsHistory />,
                handle: { pageName: "Bank Details - Deposits" },
              },
            ],
          },
          {
            path: "subscriptions",
            element: <Payments.SubscriptionsTab />,
          },
          {
            path: "monthly-invoice",
            element: <Payments.MonthlyInvoice />,
          },
          {
            path: "subscriptions",
            element: <Payments.Subscriptions />,
          },
        ],
      },
      {
        path: "subscriptions/subscription-plan",
        handle: { pageName: "Subscription Plan" },
        element: <Payments.SubscriptionPlanPage />,
      },
    ],
  },
  {
    path: "trip-reporting",
    element: <RequirePermission permission={EPermission.ProviderReportingTripManage} />,
    handle: { pageName: "Trip Reporting" },
    children: [
      {
        index: true,
        element: <TripReporting />,
      },
    ],
  },
  {
    path: "private-network/*",
    element: <Navigate to="../rm-network/private-network" replace />,
  },
  {
    path: "rm-network",
    element: <RequirePermission permission={EPermission.ProviderPrivateNetworkManage} />,
    handle: { pageName: "RM Network" },
    children: [
      {
        element: <PrivateNetwork />,
        children: [
          { index: true, element: <Navigate to="private-network" replace /> },
          {
            path: "private-network",
            handle: { pageName: "Private Network" },
            element: <PrivateNetwork.Active />,
          },
          {
            path: "archive-network",
            handle: { pageName: "Archived Network" },
            element: <PrivateNetwork.Archived />,
          },
        ],
      },
      {
        path: ":id/history",
        handle: { pageName: "History Logs" },
        element: <PrivateNetwork.History />,
      },
      {
        path: ":id/fleet-leaders/:networkOperatorId",
        handle: { pageName: "Fleet Leaders" },
        element: <PrivateNetwork.FleetLeaders />,
      },
    ],
  },
  {
    path: "profile",
    handle: { pageName: "Profile" },
    children: [
      {
        index: true,
        element: <Profile />,
      },
      {
        path: "edit",
        element: <Profile.Edit />,
      },
      {
        path: "signup-success",
        element: <Profile.SignUpSuccess />,
      },
      {
        path: "payment-method-setup",
        element: <Profile.PaymentMethodSetup />,
      },
    ],
  },
  {
    path: "bookings",
    element: <RequirePermission permission={EPermission.ProviderClientManage} />,
    handle: { pageName: "Bookings" },
    children: [
      {
        index: true,
        element: <Bookings />,
      },
    ],
  },
  {
    path: "onboarding",
    handle: { pageName: "Onboarding" },
    children: [
      {
        index: true,
        element: <Onboarding />,
      },
    ],
  },
  {
    path: "vehicles",
    element: <RequirePermission permission={EPermission.ProviderConfigVehicleManage} />,
    handle: { pageName: "Vehicles" },
    children: [
      {
        path: "*",
        element: <Vehicles />,
        children: [
          {
            index: true,
            element: <Navigate to="my-vehicles" replace />,
          },
          {
            path: "my-vehicles",
            handle: { pageName: "My Vehicles" },
            children: [
              {
                index: true,
                element: <Vehicles.MyVehicles />,
              },
              {
                path: "add/:vehicleId",
                handle: { pageName: "Add Vehicle" },
                element: <Vehicles.MyVehicles.AddVehicle />,
              },
              {
                path: ":vehicleId",
                element: <Vehicles.MyVehicles.ViewVehicle />,
              },
              {
                path: ":vehicleId/edit",
                handle: { pageName: "Edit Vehicle" },
                element: <Vehicles.MyVehicles.EditVehicle />,
              },
            ],
          },
          {
            path: "affiliate-vehicles",
            handle: { pageName: "My Drivers' Vehicles" },
            children: [
              {
                index: true,
                element: <Vehicles.AffiliateVehicles />,
              },
              {
                path: ":vehicleId",
                element: <Vehicles.AffiliateVehicles.ViewVehicle />,
              },
              {
                path: ":vehicleId/edit",
                handle: { pageName: "Edit Vehicle" },
                element: <Vehicles.AffiliateVehicles.EditVehicle />,
              },
            ],
          },
          {
            path: "archived-vehicles",
            handle: { pageName: "Archived Vehicles" },
            children: [
              {
                index: true,
                element: <Vehicles.ArchivedVehicles />,
              },
              {
                path: ":vehicleId",
                element: <Vehicles.ArchivedVehicles.ViewVehicle />,
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "vehicles-legacy",
    element: <RequirePermission permission={EPermission.ProviderConfigVehicleManage} />,
    handle: { pageName: "Vehicles (Legacy)" },
    children: [
      {
        path: "*",
        element: <LegacyVehicles />,
        children: [
          {
            index: true,
            element: <Navigate to="my-vehicles" replace />,
          },
          {
            path: "my-vehicles",
            handle: { pageName: "My Vehicles" },
            children: [
              {
                index: true,
                element: <LegacyVehicles.MyVehicles />,
              },
              {
                path: ":vehicleId",
                element: <Vehicles.MyVehicles.ViewVehicle />,
              },
            ],
          },
          {
            path: "affiliate-vehicles",
            handle: { pageName: "My Drivers' Vehicles" },
            children: [
              {
                index: true,
                element: <LegacyVehicles.AffiliateVehicles />,
              },
            ],
          },
          {
            path: "archived-vehicles",
            handle: { pageName: "Archived Vehicles" },
            children: [
              {
                index: true,
                element: <LegacyVehicles.ArchivedVehicles />,
              },
              {
                path: ":vehicleId",
                element: <Vehicles.ArchivedVehicles.ViewVehicle />,
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "onboarding",
    handle: { pageName: "Onboarding" },
    children: [
      {
        index: true,
        element: <Onboarding />,
      },
    ],
  },
  {
    path: "optimizations",
    handle: { pageName: "Optimizations" },
    children: [
      {
        index: true,
        element: <Onboarding />,
      },
    ],
  },
  {
    path: "suspended",
    handle: { pageName: "Suspended" },
    children: [
      {
        index: true,
        element: <Suspended />,
      },
    ],
  },
  {
    path: "subscription-required",
    handle: { pageName: "Subscription Required" },
    children: [
      {
        index: true,
        element: <SubscriptionRequired />,
      },
    ],
  },
  {
    path: "add-ons",
    handle: { pageName: "Add Ons" },
    element: <RequirePermission permission={EPermission.ProviderAddOnsManage} />,
    children: [
      {
        index: true,
        element: <AddOns />,
      },
    ],
  },
];
